import { transition, trigger, useAnimation } from '@angular/animations';
import { Component, effect, inject, input, ChangeDetectionStrategy } from '@angular/core';

import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, filter, map, switchAll } from 'rxjs/operators';

import { Store } from '@ngrx/store';

import { AsyncPipe } from '@angular/common';
import { YouTubePlayer } from '@angular/youtube-player';
import { isNotNullOrUndefined } from '@rocksoft/shared';
import { pulseUpdate } from '../../../../shared/animations/pulse.animation';
import { MediaStateChangedEvent } from '../../../../shared/events/media-state-changed.event';
import { ButtonPlayerActionCompleted } from '../../../../stores/buttons/buttons.action';
import { getButtonPressForRoom } from '../../../../stores/buttons/buttons.selector';
import { YouTubeVideo } from './youtube-video.model';
import { YouTubeService } from './youtube.service';

@Component({
  selector: 'era-player',
  templateUrl: './player.component.html',
  styleUrls: ['./player.component.scss'],
  animations: [trigger('pulse', [transition('* => *', [useAnimation(pulseUpdate)])])],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [YouTubePlayer, AsyncPipe],
})
export class PlayerComponent {
  private readonly youTubeService = inject(YouTubeService);
  private readonly store = inject(Store);

  readonly mediaState = input.required<MediaStateChangedEvent>();
  private id: string;
  private player: YT.Player;

  private readonly _query$ = new Subject<string>();
  private readonly query$ = this._query$.asObservable();

  public buttonCode$: Observable<string | null | undefined>;
  private subscriptions: Subscription[] = [];

  constructor() {
    this.query$
      .pipe(
        debounceTime(500),
        map((query: string) => this.youTubeService.search(query)),
        switchAll()
      )
      .subscribe(
        // act on the return of the search
        (_results) => {
          if (_results.length > 0) {
            this.playVideo(_results[0]);
          }
        },
        (err) => {
          console.log(err);
        },
        () => {
          return;
        }
      );

    effect(() => {
      const mediaState = this.mediaState();
      // clear old subscriptions
      this.subscriptions.forEach((subscription) => {
        subscription.unsubscribe();
      });
      this.subscriptions = [];

      if (mediaState) {
        this.actuateMediaState();

        this.buttonCode$ = this.store.select(getButtonPressForRoom(mediaState.HotelCode, mediaState.UnitCode)).pipe(
          filter(isNotNullOrUndefined),
          map((x) => x.ButtonCode)
        );

        this.subscriptions.push(
          this.buttonCode$.pipe(filter((code) => code === 'FastRewind')).subscribe(() => {
            this.rewindVideo();
          }),
          this.buttonCode$.pipe(filter((code) => code === 'FastForward')).subscribe(() => {
            this.forwardVideo();
          }),
          this.buttonCode$.pipe(filter((code) => code === 'Play')).subscribe(() => {
            this.play();
          }),
          this.buttonCode$.pipe(filter((code) => code === 'Pause')).subscribe(() => {
            this.pauseVideo();
          })
        );
      }
    });
  }

  savePlayer(player) {
    this.player = player;
    if (this.id) {
      this.player.loadVideoById(this.id);
    }
  }

  public playSearch(query: string): void {
    this._query$.next(query);
  }

  private actuateMediaState() {
    this.setVolume(this.mediaState().Volume ?? 0);
    if (this.mediaState().Muted) {
      this.muteVolume();
    }
  }

  private rewindVideo() {
    if (!this.player) {
      return;
    }

    const time = this.player.getCurrentTime() - 5;
    this.player.seekTo(time, true);
    this.player.playVideo();
    this.store.dispatch(
      new ButtonPlayerActionCompleted({
        HotelCode: this.mediaState().HotelCode,
        UnitCode: this.mediaState().UnitCode,
      })
    );
  }

  private play() {
    if (!this.player) {
      return;
    }

    this.player.playVideo();
    this.store.dispatch(
      new ButtonPlayerActionCompleted({
        HotelCode: this.mediaState().HotelCode,
        UnitCode: this.mediaState().UnitCode,
      })
    );
  }

  private pauseVideo() {
    if (!this.player) {
      return;
    }

    this.player.pauseVideo();
  }

  private forwardVideo() {
    if (!this.player) {
      return;
    }

    const time = this.player.getCurrentTime() + 5;
    this.player.seekTo(time, true);
    this.player.playVideo();
    this.store.dispatch(
      new ButtonPlayerActionCompleted({
        HotelCode: this.mediaState().HotelCode,
        UnitCode: this.mediaState().UnitCode,
      })
    );
  }

  private muteVolume() {
    if (!this.player) {
      return;
    }

    this.player.mute();
  }

  private setVolume(volume: number) {
    if (!this.player) {
      return;
    }

    this.player.setVolume(volume);
    if (this.player.isMuted()) {
      this.player.unMute();
    }
  }

  private playVideo(video: YouTubeVideo) {
    if (!this.player) {
      return;
    }

    if (video) {
      this.id = video.id;
      this.player.loadVideoById(this.id);
    }
  }
}
